/**
 * SWOLF (strokes + duration_seconds) aggregates. Lower = more efficient.
 * Sources directly from `HKSWOLFScore` lap metadata via the Lap shape.
 *
 * The "silk sets" framing is Scott's project vocabulary (renamed from "silk
 * strokes" on 2026-05-29 — testers found "strokes" confusing) — the top-3
 * lowest-SWOLF lengths in the workout, often coinciding with phase starts
 * (post-rest, post-warm-up, pre-fatigue). The `silkAtPhaseStart` field powers
 * the "first-stroke phenomenon" callout in P6Swolf.
 */
import type { Lap } from "./laps";
import type { WorkoutPhase } from "./phases";

export interface SwolfStats {
  /**
   * Scott's "silk sets" — top lengths by SWOLF efficiency (lowest score).
   * Named after the felt sense of gliding without fighting the water.
   * Empirically: tend to happen at the START of a phase (post-rest, post-warm-up,
   * pre-fatigue) — which is why pace + low stroke count combine on these lengths.
   */
  bestLaps: Lap[];
  /** Worst laps by SWOLF. Top 3. */
  worstLaps: Lap[];
  /** Per stroke style: the two RAW facts (avg strokes per length, avg seconds
   *  per length) plus the fused SWOLF (kept for internal ranking only). */
  perStrokeAvg: { stroke: string; avgSwolf: number; avgStrokes: number; avgSeconds: number; lapCount: number }[];
  /** Overall workout-average SWOLF. */
  overallAvg: number;
  /** How many of the silk sets (bestLaps) are the FIRST lap of a phase.
   *  Used to surface the "first-stroke phenomenon" callout. */
  silkAtPhaseStart: number;
}

type SwolfLap = Lap & { swolf: number };

export function computeSwolfStats(laps: Lap[], phases: WorkoutPhase[]): SwolfStats | null {
  // DRILL-03: exclude effectiveExcluded laps from SWOLF aggregates.
  const lapsWithSwolf: SwolfLap[] = laps.filter(
    (l): l is SwolfLap => l.swolf !== null && !l.effectiveExcluded,
  );
  if (lapsWithSwolf.length === 0) return null;

  const sortedBest = [...lapsWithSwolf].sort((a, b) => a.swolf - b.swolf);
  const sortedWorst = [...lapsWithSwolf].sort((a, b) => b.swolf - a.swolf);
  const perStrokeMap = new Map<string, { swolf: number; strokes: number; seconds: number; count: number }>();
  for (const lap of lapsWithSwolf) {
    const cur = perStrokeMap.get(lap.strokeStyle) ?? { swolf: 0, strokes: 0, seconds: 0, count: 0 };
    cur.swolf += lap.swolf;
    cur.strokes += lap.spl ?? lap.strokeCount ?? 0; // strokes for this length
    cur.seconds += lap.durationSeconds;             // seconds for this length
    cur.count += 1;
    perStrokeMap.set(lap.strokeStyle, cur);
  }
  const perStrokeAvg = [...perStrokeMap.entries()]
    .map(([stroke, { swolf, strokes, seconds, count }]) => ({
      stroke,
      avgSwolf: swolf / count,
      avgStrokes: strokes / count,
      avgSeconds: seconds / count,
      lapCount: count,
    }))
    .sort((a, b) => a.avgSwolf - b.avgSwolf);
  const overallAvg = lapsWithSwolf.reduce((s, l) => s + l.swolf, 0) / lapsWithSwolf.length;
  const bestLaps = sortedBest.slice(0, 3);

  const phaseStartLapIndices = new Set(phases.map((p) => p.lapStart));
  const silkAtPhaseStart = bestLaps.filter((l) => phaseStartLapIndices.has(l.index)).length;

  return {
    bestLaps,
    worstLaps: sortedWorst.slice(0, 3),
    perStrokeAvg,
    overallAvg,
    silkAtPhaseStart,
  };
}
